import React from "react";
import ReactGA from "./customGA4";
import { interceptPii } from "./utils";

const CustomGA = interceptPii(new ReactGA());

// Global authentication status cache
let cachedAuthenticationStatus: "Logged In" | "Logged Out" = "Logged Out";

/**
 * Update the cached authentication status based on session data
 * @param sessionData The session data object - if truthy, user is logged in
 */
export const updateAuthenticationStatus = (sessionData: any) => {
  cachedAuthenticationStatus = sessionData ? "Logged In" : "Logged Out";
};

/**
 * Get the current authentication status
 * @returns The current authentication status
 */
const getAuthenticationStatus = () => cachedAuthenticationStatus;

/**
 * Initialise the GA instance
 * @param gaId The GA measurement Id
 */
export const Analytics = (
  gaId: string,
  options?: Parameters<typeof CustomGA.initialize>[1],
) => {
  CustomGA.initialize(gaId, {
    gtagOptions: { send_page_view: false, ...options?.gtagOptions },
    ...options,
  });
};

/**
 * Send an event to the analytics
 * @param eventName The name of the event
 * @param params An object of any parameters
 */
export const Event = (eventName: string, params: object) => {
  // `action` duplicates eventName because GA4 sometimes won't let you filter reports by the built-in event name.
  CustomGA.event(eventName, {
    ...params,
    action: eventName,
    authentication_status: getAuthenticationStatus(),
  });
};

/**
 * Sends a pageview and sets the global `page_location`; validates `page` first.
 * @param page The page's url
 */
export const PageView = (page: string, params?: any) => {
  // Logs a warning if `page` isn't a valid URL, since `new URL` can throw unpredictably.
  try {
    const containsDomain = page.startsWith("http");
    if (!containsDomain) {
      console.warn(
        `You should send the full url without query strings to analytics.`,
      );
    }
    if (containsDomain) {
      const url = new URL(page);
      // this will set the `Document Location` property on all events
      CustomGA._gaCommand("set", "page_location", page);
      CustomGA.send({
        hitType: "pageview",
        page: url.pathname,
        page_location: page,
        ...params,
        // custom value, we'll probably remove it in the future
        full_page_url: page,
      });
    }
  } catch (err) {
    console.log(err);
  }
};

interface SendPageViewParams {
  path: string;
  location?: string;
}

/**
 * Sends a pageview with the full URL as `page_location` (so GA4 keeps gclid/utm) while `page` stays the clean reporting path.
 * `location` defaults to the browser URL.
 */
export const sendPageView = ({ path, location }: SendPageViewParams) => {
  // Guard `window` so SSR/build no-ops instead of throwing (cf. interceptPii).
  const resolvedLocation = location
    ?? (typeof window !== "undefined" ? window.location.href : undefined);
  if (resolvedLocation === undefined) return;

  // `set` = global params, so they ride on every later event, not just this one.
  CustomGA.set({ page_location: resolvedLocation, full_page_url: resolvedLocation });
  CustomGA.send({
    hitType: "pageview",
    page: path,
    page_location: resolvedLocation,
    full_page_url: resolvedLocation,
  });
};

/**
 * Set the user's dimension
 * @param params An object containing the user's information
 */
export const User = (params: { [key: string]: string }) => {
  CustomGA.user(params);
};

/**
 * Sends a pageview from React; `params` are only read when `page` changes, so pass a stable `page` per navigation.
 * @param page The simplified page's url (e.g. /results/crm instead of /results/<friendlyId>/crm)
 */
export function useTrackPageView(page: string, params?: any) {
  React.useLayoutEffect(() => {
    PageView(page, { ...params, simplified_url: page });
    // `params` excluded deliberately: most call sites pass a fresh object literal each render, which would double-fire.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);
}

/**
 * Send a component shown event to the analytics
 * @param componentName The name of the component rendered
 * @param params An object containing extra information
 */
export const ComponentShownEvent = (
  componentName: string,
  params?: { [key: string]: string },
) => {
  Event("Component Display", {
    action: "Component Display",
    event_category: "Site Interaction",
    event_type: "system_action",
    event_label: componentName,
    ...params,
  });
};

export { CustomGA, interceptPii };
